import { useDepot } from '#~/depot/hooks.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { setupIntercom } from '#~/intercom/intercom.js';
import { getOrganizationSummaries, getStudioSummaries } from '#~/posthog/utils/summaries.js';
import type { Organization } from '@studio/models/organization';
import type { RoomRecord } from '@studio/models/room';
import type { User } from '@studio/models/user';
import { useEffect, useMemo, useRef } from 'react';
import { shallow } from 'zustand/shallow';

type DepotSlice = [Record<string, Organization>, Record<string, RoomRecord>, User | undefined];

const formatUserDisplayName = (user: User): string => {
  const parts = [user.firstName, user.lastName].filter((part): part is string => !!part);
  return parts.length > 0 ? parts.join(' ') : (user.email ?? user.id);
};

/**
 * Syncs user and organization data with Intercom.
 */
export const useIntercomSync = (): void => {
  const [organizations, roomRecords, user]: DepotSlice = useDepot(
    (µ) => [µ.organizations, µ.roomRecords, µ.user],
    shallow
  );

  const studioSummaries = useMemo(() => getStudioSummaries(roomRecords), [roomRecords]);
  const orgSummaries = useMemo(
    () => getOrganizationSummaries(organizations, studioSummaries),
    [organizations, studioSummaries]
  );

  const signature = useMemo(() => {
    if (!user) {
      return null;
    }

    return JSON.stringify({
      user: { id: user.id, email: user.email },
      orgs: orgSummaries.map(({ id }) => id)
    });
  }, [orgSummaries, user]);

  const lastSignature = useRef<string | null>(null);

  useEffect(() => {
    if (isElectronTrayWindow() || !user || !signature || signature === lastSignature.current) {
      if (!user) {
        lastSignature.current = null;
      }

      return;
    }

    lastSignature.current = signature;

    setupIntercom({
      email: user.email,
      name: formatUserDisplayName(user),
      userId: user.id,
      avatar: user.profilePhotoUrl,
      companies:
        orgSummaries.length > 0
          ? orgSummaries.map((org) => ({ id: org.id, name: org.name }))
          : undefined
    });
  }, [orgSummaries, signature, user]);
};
